<template>
  <div>
    <mds-page-shell
      :logo="getProductLogo()"
      :logo-responsive="getProductLogo()"
      logo-href="/"
      logo-alt="Back to home page"
    >
      <div class="createaccount-main">
        <div class="createaccount-registration-main">
          <div class="createaccount-registration-form">
            <div class="createaccount-registration-form-content-container">
              <div class="createaccount-title-for-registration-form">
                <h1 class="createaccount-registration-page-title">
                  Create your account
                </h1>
              </div>
              <div class="createaccount-registration-top-part-text">
                <p class="createaccount-registration-form-text-first">
                  Welcome to <i>Morningstar {{ currentProductName }}</i
                  >. In just minutes, you will have immediate access to your
                  digital subscription.
                </p>
                <p
                  v-if="!this.$store.state.user.LoggedIn"
                  class="createaccount-signSection"
                >
                  If you already subscribe to a Morningstar product,
                  <a
                    class="createaccount-registration-hyperlink-test"
                    target="_blank"
                    @click="login()"
                    >sign in here</a
                  >
                  for a faster checkout.
                </p>
                <p class="createaccount-print-registration-section">
                  If you are a print subscriber visiting for the first time,
                  complete a
                  <router-link
                    class="createaccount-registration-hyperlink-test"
                    :to="{
                      name: 'print-registration',
                    }"
                    >one-time registration</router-link
                  >
                </p>
              </div>
              <div class="createaccount-loader">
                <mds-loader
                  v-if="isLoading"
                  aria-label="Large Loader"
                  size="large"
                ></mds-loader>
              </div>
              <div
                :class="
                  !this.$store.state.user.LoggedIn
                    ? 'createaccount-registration-form-fields'
                    : 'createaccount-registration-form-fields-after-login'
                "
              >
                <mds-input
                  class="input-tab"
                  size="large"
                  label="First Name"
                  type="text"
                  required
                  :error="firstNameError"
                  v-model.trim="firstname"
                ></mds-input>
                <span class="FormsError" v-if="firstNameError"
                  >First name is required</span
                >
                <br />
                <mds-input
                  v-model.trim="lastname"
                  type="text"
                  size="large"
                  :error="lastNameError"
                  label="Last Name"
                  required
                ></mds-input>
                <span class="FormsError" v-if="lastNameError"
                  >Last name is required</span
                >
                <br />
                <mds-input
                  v-model.trim="email"
                  :error="emailError"
                  size="large"
                  label="Email"
                  type="email"
                  required
                  :disabled="existingUser"
                ></mds-input>
                <span class="FormsError" v-if="emailError">{{
                  emailErrorMessage
                }}</span>
                <br />
                <mds-input
                  v-model.trim="password"
                  :error="passwordError"
                  password
                  size="large"
                  label="Password"
                  required
                  v-if="!disabled"
                ></mds-input>
                <span class="FormsError" v-if="passwordError">{{
                  passwordErrorMessage
                }}</span>
                <div
                  class="createaccount-registration-password-div"
                  v-if="!disabled"
                >
                  <p
                    v-bind:class="[
                      this.validatePasswordError
                        ? 'registration-password-text-error'
                        : 'registration-password-text',
                      'registration-password-text',
                    ]"
                  >
                    Your password must be 8 or more characters, with one upper
                    or lower case letter, one special character (e.g., ?$%!@#)
                    AND one number (1-9). It cannot contain more than 3
                    sequential or repeating characters, username, mstar, or
                    Morningstar.
                  </p>
                </div>

                <mds-input
                  password
                  v-model.trim="verifypassword"
                  size="large"
                  label="Verify Password"
                  :error="verifypasswordError"
                  v-if="!disabled"
                  required
                ></mds-input>
                <span class="FormsError" v-if="verifypasswordError"
                  >Password verification does not match the password
                  entered</span
                >
              </div>
              <div class="createaccount-registration-border"></div>
              <div class="createaccount-registration-checkboxes">
                <mds-checkbox size="small" value="accept" v-model="emailoptions"
                  >Get emails about new Morningstar products and features.
                </mds-checkbox>
                <br />
                <mds-checkbox
                  size="small"
                  value="accept"
                  v-model="agreementChecked"
                  >Clicking continue indicates agreement to the Morningstar
                  Investor Newsletter
                  <router-link
                    class="createaccount-registration-terms-link"
                    target="_blank"
                    :to="{ name: 'user-agreement' }"
                    >Terms of Use</router-link
                  >
                  and
                  <a
                    target="_blank"
                    class="createaccount-registration-terms-link"
                    href="https://www.morningstar.com/company/privacy-policy"
                    >Morningstar Privacy Policy</a
                  >.<span class="required">*</span>
                </mds-checkbox>
                <mds-alert
                  @mds-alert-dismissed="closeAlert()"
                  variation="error"
                  class="agreetermError"
                  v-if="agreetermError"
                  :tinted="agreetermError"
                >
                  You must agree to the Morningstar Terms of Use
                </mds-alert>
              </div>
              <div class="createaccount-registration-button-container">
                <div>
                  <mds-button-container left-aligned>
                    <mds-button
                      variation="secondary"
                      size="large"
                      type="button"
                      @click="
                        $router.push({
                          name: 'combined-home',
                          params: { currentproduct: 'Common' },
                        })
                      "
                    >
                      Cancel
                    </mds-button>
                    <div class="continue-button">
                      <mds-button
                        variation="primary"
                        size="large"
                        type="button"
                        right-aligned
                        @click.native.stop="ContinueForm"
                        :disabled="!agreementChecked || isLoading"
                      >
                        Continue</mds-button
                      >
                    </div>
                  </mds-button-container>
                </div>
              </div>
            </div>
            <div class="createaccount-register-page-bottom"></div>
          </div>
        </div>
      </div>
    </mds-page-shell>
  </div>
</template>

<script>
import MdsPageShell from "@mds/page-shell";
import MdsInput from "@mds/input";
import MdsCheckbox from "@mds/checkbox";
import { MdsButton, MdsButtonContainer } from "@mds/button";
import MdsAlert from "@mds/alert";
import MdsLoader from "@mds/loader";
import EncryptDecryptMixin from "../../mixins/EncryptDecryptMixin";
import { fetchData, postData /*, putData*/ } from "@/utilities/API/apiUtility";
import {  getUserDetailsFromStripe,getCustomerFromStripe, getGUID } from "@/utilities/stripe";
import { AddEmailAddressInSession, AddCustIdInSession, GetSessionData, AddUIMIdInSession } from "@/utilities/userDataUtility";
export default {
  name: "registration-section",
  mixins: [EncryptDecryptMixin],
  components: {
    MdsPageShell,
    MdsInput,
    MdsCheckbox,
    MdsButton,
    MdsButtonContainer,
    MdsAlert,
    MdsLoader,
  },
  data() {
    return {
      liststripeCountry: this.$store.state.country.stripeCountryList,
      currentProductName: "",
      currentProducts: "",
      ProductCode: null,
      userDetails: [],
      userRoles: [],
      userConsents: [],
      userEmail: null,
      MEIAccess: false,
      MDIAccess: false,
      MSIAccess: false,
      MFIAccess: false,
      userID: null, // GUID [UIM]
      firstname: null,
      lastname: null,
      tempFname: null,
      tempLname: null,
      email: null,
      password: null,
      verifypassword: null,
      disabled: false,
      agreementChecked: false,
      emailoptions: false,
      firstNameError: false,
      lastNameError: false,
      emailError: false,
      emailErrorMessage: "",
      passwordError: false,
      passwordErrorMessage: "",
      verifypasswordError: false,
      validatePasswordError: false,
      existingUser: false,
      agreetermError: false,
      currentProduct: null,
      isLoading: true,

      IsUserInStripe: false,
      isInvestorUser: false,
      CustomerID: null, // Stripe customer Id
      createUIMUserURL:
        process.env.VUE_APP_BackEndAPI + process.env.VUE_APP_CREATE_UIM_USER,
      updateUIMUSerURL:
        process.env.VUE_APP_BackEndAPI + process.env.VUE_APP_UPDATE_UIM_USER,
      createStripeUserURL:
        process.env.VUE_APP_BackEndAPI + process.env.VUE_APP_CreateStripeUser,
      isUserInUIMURL:
        process.env.VUE_APP_BackEndAPI +
        process.env.VUE_APP_GET_USER_INFO_BY_EMAIL,
       
    };
  },
  methods: {
    // async getEmailNotificationList(customerId) {
    //   this.isLoading = true;
    //   var GetEmailNotificationUrl =
    //     process.env.VUE_APP_BackEndAPI + process.env.VUE_APP_EmailNotification;
    //   GetEmailNotificationUrl = GetEmailNotificationUrl.replace(
    //     "{customerId}",
    //     customerId
    //   );
    //   try {
    //     return fetchData(GetEmailNotificationUrl)
    //       .then((response) => {
    //         if (response) {
    //           if (response.status == 200) {
    //             if (response.data) {
    //               var responseData = response.data;
    //               if (responseData) {
    //                 for (var i in responseData) {
    //                   var alertOptions = responseData[i];
    //                   if (alertOptions) {
    //                     if (
    //                       alertOptions.OptionID == 19 &&
    //                       alertOptions.OptionValue == 1
    //                     ) {
    //                       this.emailoptions = true;
    //                     }
    //                   }
    //                 }
    //               }
    //             }
    //           }
    //         }
    //       })
    //       .catch((error) => {
    //         if (process.env.NODE_ENV == "development") {
    //           console.log(error);
    //         }
    //       })
    //       .finally();
    //   } catch (ex) {
    //     if (process.env.NODE_ENV == "development") {
    //       console.log(ex.message);
    //     }
    //   }
    // },
    closeAlert() {
      this.agreetermError = false;
    },
    getProductLogo() {
      var images = require.context("../../../assets/Images", false, /\.svg$/);
      return images("./Common.svg");
    },
    getSelectedProduct(productCode) {
      if (productCode.includes("MSI")) {
        this.currentProducts = "MSI";
        this.currentProductName = "StockInvestor";
      }
      if (productCode.includes("MDI")) {
        this.currentProducts = this.currentProducts == "" ? "MDI" : ",MDI";
        this.currentProductName = "DividendInvestor";
      }
      if (productCode.includes("MFI")) {
        this.currentProducts = this.currentProducts == "" ? "MFI" : ",MFI";
        this.currentProductName = "FundInvestor";
      }
      if (productCode.includes("MEI")) {
        this.currentProducts = this.currentProducts == "" ? "MEI" : ",MEI";
        this.currentProductName = "ETFInvestor";
      }
      this.currentProductName = this.currentProducts.includes(",")
        ? "Newsletters"
        : this.currentProductName;
    },
    async getDetailsFromStripe(product, userID) {
      if (this.$store.state.user.LoggedIn) {
        this.userDetails = await getUserDetailsFromStripe(product, userID);
      } else {
          const sessionData = await GetSessionData(this.$session);
          if(sessionData != null && sessionData != undefined){
            const customerID = sessionData.StripeCustId;          
        if(customerID){
          this.userDetails = await getCustomerFromStripe(customerID);
        }
      }
      }
      if (this.userDetails && this.userDetails != "") {
        this.tempFname = this.userDetails.metadata.mstar_fname;
        this.tempLname = this.userDetails.metadata.mstar_lname;
        this.firstname = this.userDetails.metadata.mstar_fname;
        this.lastname = this.userDetails.metadata.mstar_lname;
        this.email = this.userDetails.email;
        this.CustomerID = this.userDetails.id;
        if(this.email != null && this.email != "" && this.email != undefined){
          AddEmailAddressInSession(this.email,this.$session);
        }
        //this.saveDataTolocalStorage({ Email: this.email });
      }
    },
    async getUserDetails(userID) {
      this.isInvestorUser = this.$session.get("IsInvestorUser");
      if (this.IsUserInStripe) {
        await this.getDetailsFromStripe("newsletters", userID);
        return;
      }

      if(!this.IsUserInStripe){
        if(!this.isInvestorUser){
        // Double-check the investor's case to see if it returned false.
          await this.onlyForInvestorUser(userID);
        }
      }
      if (this.isInvestorUser) {
      //  await this.getDetailsFromStripe("investor", userID);
       this.email = this.userEmail;
       return;
      }

      // FOR OMAHA USER
      var getUserDetailsAPIURL =
        process.env.VUE_APP_BackEndAPI + process.env.VUE_APP_GetUserDetails;
      getUserDetailsAPIURL = getUserDetailsAPIURL.replace("{userId}", userID);

      try {
        const response = await fetchData(getUserDetailsAPIURL, {
          headers: { Accept: "application/json" },
        });

        if (response?.status === 200 && response.data) {
          this.userDetails = response.data;
          this.firstname = this.userDetails["FirstName"];
          this.lastname = this.userDetails["LastName"];
          this.email = this.userDetails["Email"];
        }
      } catch (error) {
        if (process.env.NODE_ENV == 'development') {
          console.error("Error fetching user details:", error.message);
        }
      }
    },
    async checkuseremailExists(userEmail) {
      var getUserRoleAPIURL =
        process.env.VUE_APP_BackEndAPI + process.env.VUE_APP_GetUserEmail;
      getUserRoleAPIURL = getUserRoleAPIURL.replace("{UserEmail}", userEmail);
      try {
        return fetchData(getUserRoleAPIURL, {
          headers: {
            Accept: "application/json",
          },
        })
          .then((response) => {
            if (response) {
              if (response.status == 200) {
                if (response.data) {
                  this.userRoles = response.data;
                  var uid = this.userRoles["UserID"];
                  if (uid != null) {
                    return true;
                  } else {
                    return false;
                  }
                }
              }
            }
          })
          .catch((error) => {
            if (process.env.NODE_ENV == 'development') {
                  console.log(error);
            }
          });
      } catch (ex) {
        if (process.env.NODE_ENV == 'development') {
          console.log(ex.message);
        }
      }
    },
    validatePassword(password) {
      if (password.length >= 8 && password.length <= 24) {
        if (/\d+/.test(password) && /[A-Za-z]/.test(password)) {
          if (/[^\da-zA-Z]/.test(password)) {
            if (/(.)\1{3}/.test(password)) {
              return false;
            }
            if (this.hasSequentialPattern(password)) {
              return false;
            }

            const sequentialPatterns = [
              "abcd", "bcde", "cdef", "defg", "efgh", "fghi", "ghij", "hijk", "ijkl", "jklm", "klmn", "lmno",
              "mnop", "nopq", "opqr", "pqrs", "qrst", "rstu", "stuv", "tuvw", "uvwx", "vwxy", "wxyz",
              "1234", "2345", "3456", "4567", "5678", "6789", "7890", "8901",
              "qwert", "werty", "erty", "rtyu", "tyui", "yuiop", "uiop",
              "password", "letmein", "abc123", "qwerty", "iloveyou", "admin", "welcome", "welcome123"
            ];

            for (let pattern of sequentialPatterns) {
              const regex = new RegExp(pattern, "i");
              if (regex.test(password)) {
                return false;
              }
            }

            if (this.firstname != null && this.firstname != "") {
              if (password.toLowerCase().includes(this.firstname.toLowerCase())) {
                return false;
              } else {
                return true;
              }
            } else {
              return true;
            }
          } else {
            return false;
          }
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
    hasSequentialPattern(password) {
      const passwordLower = password.toLowerCase();
      const length = passwordLower.length;

      for (let i = 0; i < length - 3; i++) {
        const sequence = passwordLower.substring(i, i + 4);

        const isSequential = sequence.split('').every((char, index, arr) => {
          return arr[index].charCodeAt(0) === arr[0].charCodeAt(0) + index;
        });

        if (isSequential) {
          return true;
        }
      }

      return false;
    },
    async GetUserConsents(UserId) {      
      var GetUserConsentsUrl =
        process.env.VUE_APP_BackEndAPI + process.env.VUE_APP_GetUserConsents;
      GetUserConsentsUrl = GetUserConsentsUrl.replace(
        "{userId}",
        UserId.toUpperCase()
      );
      try {
        const response = await fetchData(GetUserConsentsUrl);

        if (response && response.status == 200) {
          this.userConsents = response.data;
          this.saveUserConsentsTolocalStorage({ UserConsents: this.userConsents });
        }
      }  catch (error) {
        if (process.env.NODE_ENV == 'development') {
          console.log("Error in UpdateUserConsents:", error);
        }
      }
      
    },
    async UpdateUserConsents(UserId, EmailConsent) {
      this.$gtag.event("create_account", {
        product: this.currentProducts,
        userid: UserId,
      });
      let updateUserConsents = false;
      this.isLoading = true;      

      try {       
        if (this.userConsents == null || this.userConsents == undefined || this.userConsents.length == 0) {
          if (EmailConsent === true) {
            updateUserConsents = true;
            this.userConsents = {}; // Initialize as an object, not an array
            this.userConsents.user = UserId;
            this.userConsents.Ids = []; // Initialize Ids as an empty array
            this.userConsents.Ids.push("email-alerts-opt-in");
          }
        } else {
          if (
            EmailConsent === true &&
            !this.userConsents.Ids.includes("email-alerts-opt-in")
          ) {
            updateUserConsents = true;
            this.userConsents.Ids.push("email-alerts-opt-in");
          } else if (
            EmailConsent === false &&
            this.userConsents.Ids.includes("email-alerts-opt-in")
          ) {
            updateUserConsents = true;
            const indexToRemove = this.userConsents.Ids.indexOf(
              "email-alerts-opt-in"
            );
            if (indexToRemove !== -1) {
              this.userConsents.Ids.splice(indexToRemove, 1);
            }
          }
        }
        this.userConsents.emailAddress = this.email;
        if (updateUserConsents == true) {
          var AddUpdateUserConsentsUrl =
            process.env.VUE_APP_BackEndAPI +
            process.env.VUE_APP_AddUpdateUserConsents;

          const postResponse = await postData(
            AddUpdateUserConsentsUrl,
            this.userConsents
          );

          if (!postResponse || postResponse.status !== 200) {
            if (process.env.NODE_ENV == 'development') {
              console.log("Error updating consents");
            }
          } else {
            this.saveUserConsentsTolocalStorage({ UserConsents: this.userConsents });
          }
        }
        
      } catch (error) {
        if (process.env.NODE_ENV == 'development') {
          console.log("Error in UpdateUserConsents:", error);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async AddUserAgreements(UserId) {
      this.isLoading = true;
      var AddUserAgreementsUrl =
        process.env.VUE_APP_BackEndAPI + process.env.VUE_APP_AddUserAgreements;

      try {
        if (this.agreementChecked) {
          this.userConsents = {}; // Initialize as an object, not an array
          this.userConsents.user = UserId;
          this.userConsents.Ids = []; // Initialize Ids as an empty array
          const now = new Date();
          const formattedDateTime = `tos_${now
            .getFullYear()
            .toString()
            .slice(2)}${String(now.getMonth() + 1).padStart(2, "0")}${String(
            now.getDate()
          ).padStart(2, "0")}_T${String(now.getHours()).padStart(
            2,
            "0"
          )}${String(now.getMinutes()).padStart(2, "0")}${String(
            now.getSeconds()
          ).padStart(2, "0")}`;
          this.userConsents.Ids.push(formattedDateTime);

          const postResponse = await postData(
            AddUserAgreementsUrl,
            this.userConsents
          );

          if (!postResponse || postResponse.status !== 200) {
            if (process.env.NODE_ENV == 'development') {
              console.log("Error updating agreements");
            }
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV == 'development') {
          console.log("Error in UpdateUserConsents:", error);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async ContinueForm() {
      this.isLoading = true;

      this.resetErrors();
      this.validateBasicFields();

      if (!this.existingUser) {
        await this.validateNewUserFields();
      }

      if (!this.hasErrors()) {
        await this.processForm();
      } else {
        this.isLoading = false;
      }
    },
    resetErrors() {
      this.firstNameError = false;
      this.lastNameError = false;
      this.emailError = false;
      this.emailErrorMessage = "";
      this.passwordError = false;
      this.passwordErrorMessage = "";
      this.validatePasswordError = false;
      this.verifypasswordError = false;
      this.agreetermError = false;
    },
    validateBasicFields() {
      if (!this.firstname || this.firstname.length > 60)
        this.firstNameError = true;
      if (!this.lastname || this.lastname.length > 60)
        this.lastNameError = true;

      if (!this.email) {
        this.emailError = true;
        this.emailErrorMessage = "Email is required";
      } else if (!/^[^@]+@\w+(\.\w+)+\w$/.test(this.email)) {
        this.emailError = true;
        this.emailErrorMessage = "Email is not in correct format";
      }

      if (!this.agreementChecked) this.agreetermError = true;
    },
    async validateNewUserFields() {
      if (this.email) {
        const guidAlreadyCreated = await this.verifyGuidCreated();
        if (
          (await this.checkuseremailExists(this.email)) &&
          !guidAlreadyCreated
        ) {
          this.emailError = true;
          this.emailErrorMessage =
            "This email is already in use. Please either sign in or choose a different email to continue with your purchase";
        }
      }

      if (!this.password) {
        this.passwordError = true;
        this.passwordErrorMessage = "Password is required";
      } else {
        const isValid = await this.validatePassword(this.password);
        this.validatePasswordError = !isValid;
        this.passwordError = !isValid;
      }

      if (!this.verifypassword || this.password !== this.verifypassword) {
        this.verifypasswordError = true;
      } else {
        this.verifypasswordError = false;
      }
    },
    hasErrors() {
      return (
        this.firstNameError ||
        this.lastNameError ||
        this.emailError ||
        this.passwordError ||
        this.validatePasswordError ||
        this.verifypasswordError ||
        this.agreetermError
      );
    },
    async processForm() {
      if (this.$store.state.user.LoggedIn) {
        await this.processLoggedInUser();        
      } else {        
        await this.processNewUser();
      }

      await this.UpdateUserConsents(this.userID, this.emailoptions);
      await this.AddUserAgreements(this.userID);
    },
    async processLoggedInUser() {
      if (this.userDetails) {
        if (!this.IsUserInStripe && !this.isInvestorUser) {  //for omaha user
          const data = await this.createStripeCustomerPayload(
            this.userID,
            true
          );
          const isUserCreated = await this.CreateStripeUser(data);
          if (isUserCreated) this.pushToSubscriptionPage();        
        }else if(this.IsUserInStripe){ // if user exists in newsletter
          await this.updateUser();
        }else if(this.isInvestorUser){ // if user exists in investor
          const data = await this.createStripeCustomerPayload(
            this.userID,
            false
          );
          const isUserCreated = await this.CreateStripeUser(data);
          if (isUserCreated) this.pushToSubscriptionPage();
        }else{
          throw new Error("The Logged In User Is Not Available");
        }
      }
    },
    async processNewUser() {
      if (this.userID) {
        await this.updateUser();
        return;
      }

      try {
        const response = await postData(this.createUIMUserURL, {
          email: this.email,
          password: this.password,
        });

        if (response?.status === 200 && response.data) {
          this.userID = response.data.guId;
          
          const data = await this.createStripeCustomerPayload(
            this.userID,
            false
          );
          const isUserCreated = await this.CreateStripeUser(data);
          if (isUserCreated) {
            this.$gtag.event("create_account", {
              product: this.currentProducts,
              userid: this.userID,
            });  
            await AddUIMIdInSession(this.userID,this.$session);          
            this.pushToSubscriptionPage();
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV == 'development') {
          console.log("Error while generating GUID", error.message);
        }
      }
    },
    login() {
      this.$auth0.loginWithRedirect();
    },
    // saveDataTolocalStorage(data) {
    //   data = JSON.stringify(data);
    //   data = this.Encrypt(data);
    //   localStorage.setItem("u9", data); //u9 means its userDetails [ for security purpose I have used pseudo name and not evident name]
    // },
    saveUserConsentsTolocalStorage(data) {
      data = JSON.stringify(data);
      data = this.Encrypt(data);
      localStorage.setItem("userConsents", data);
    },
    async verifyGuidCreated() {
      const sessionData = await GetSessionData(this.$session);
      if(sessionData != null && sessionData != undefined){
        if (sessionData.UIMId != "" && sessionData.UIMId != null && sessionData.UIMId != undefined) {       
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      },
    
    async createStripeCustomerPayload(guId, isOmahaUser) {
      const { Mphone, Dphone, Country, Address_1, City, State, ZipCode } = this.userDetails || {};
      const data = {
        FirstName: this.firstname,
        LastName: this.lastname,
        Email: this.email,
        metadata: {
          mstar_fname: this.firstname,
          mstar_lname: this.lastname,
          mstar_product: "newsletters",
          mstar_id: guId,
          ...(Dphone && { home_phone: Dphone }),
        },
      };

      if (isOmahaUser) {
        if (Mphone) data.phone = Mphone;
        //const formattedCountry = Country === "USA" ? "US" : Country;
        const normalizedCountry = Country.trim().toLowerCase();

        const found = this.liststripeCountry.find(
          item => item.Country.trim().toLowerCase() === normalizedCountry
        );

        const formattedCountry = normalizedCountry === "usa"
          ? "US"
          : (found?.CountryCode || Country);

        const omahaUserDetails = {
          country: formattedCountry,
          addressLine1: Address_1,
          city: City,
          state: State,
          postalCode: ZipCode,
        };

        Object.keys(omahaUserDetails).forEach((key) => {
          if (omahaUserDetails[key]) {
            data[key] = omahaUserDetails[key];
          }
        });
      }
      //this.saveDataTolocalStorage({ Email: this.email });
      if(this.email != null && this.email != "" && this.email != undefined){
        AddEmailAddressInSession(this.email,this.$session);
      }
      return data;
    },
    async CreateStripeUser(CreateUserCommand) {
      try {
        const response = await postData(
          this.createStripeUserURL,
          CreateUserCommand
        );
        if (response) {
          if (response.status == 200) {
            await AddCustIdInSession(response.data.id,this.$session);
            //localStorage.setItem("c42", this.Encrypt(response.data.id)); // c42 is stripe customer id [ used pseudo name for security to avoid evident name ]
            return true;
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV == 'development') {
          console.log("Error while creating stripe customer", error);
        }
        return false;
      }
    },
    async populateForm() {
      try {
        if (this.$store.state.user.LoggedIn) {
          const response = await getUserDetailsFromStripe(
            "newsletters",
            this.userID
          );
          
          this.IsUserInStripe = response ? true : false;
          await this.getUserDetails(this.userID);
          if (this.userConsents == null || this.userConsents == undefined || this.userConsents.Ids == undefined) {
            await this.GetUserConsents(this.userID);
          }
        } else {
          // let data = this.Decrypt(localStorage.getItem("u9"));
          // if (data) {
          //   data = JSON.parse(data);
          const sessionData = await GetSessionData(this.$session);
          if(sessionData != null && sessionData != undefined){
            this.userID = sessionData.UIMId; 
            this.email = sessionData.EmailAddress;  
          }
          else{
            this.$session.remove('session-guid');
          }   
          //}
          await this.getDetailsFromStripe("newsletters", this.userID);
          let savedconsents = this.Decrypt(localStorage.getItem("userConsents"));
          if (savedconsents) {
            savedconsents = JSON.parse(savedconsents);
            this.userConsents = savedconsents.UserConsents;
          }
        }
        if (this.userConsents != null && this.userConsents != undefined && this.userConsents.Ids != undefined && this.userConsents.Ids.includes("email-alerts-opt-in")) {
           this.emailoptions = true;
        }
      } catch (error) {
        if (process.env.NODE_ENV == 'development') {
          console.error("Error in populateForm:", error);
        }
      }
    },
    async updateUser() {
      const sessionData = await GetSessionData(this.$session);
      let emailAddress = sessionData.EmailAddress;
      if (emailAddress != null && emailAddress != "") {
        if (
          this.firstname !== this.tempFname ||
          this.lastname !== this.tempLname ||
          this.email !== emailAddress
        ) {

          if (this.email !== emailAddress) {
            var guId = await getGUID(this.email);
          }
          if (guId != null) {
            this.emailError = true;
            this.emailErrorMessage =
              "This email is already in use. Please either sign in or choose a different email to continue with your purchase";
          } else {
            this.emailError = false;
            this.emailErrorMessage = null;
            try {
              const UpdateUserCommand = this.populateUpdateCustomerCommand(
                emailAddress
              );
              postData(
                process.env.VUE_APP_BackEndAPI +
                  process.env.VUE_APP_UpdateStripeUserDetails,
                UpdateUserCommand
              )
                .then((response) => {
                  if (response) {
                    if (response.status == 200) {
                      if (response.data != null && response.data !== "") {
                        this.passwordError = false;
                        this.passwordErrorMessage = "";
                        if(this.email != null && this.email != "" && this.email != undefined){
                          AddEmailAddressInSession(this.email,this.$session);
                        }
                        //this.saveDataTolocalStorage({ Email: this.email });
                        this.pushToSubscriptionPage();
                      }
                    } else {
                      if (
                        response.response.data.Message.includes(
                          "Please enter your current password"
                        )
                      ) {
                        this.passwordError = true;
                        this.passwordErrorMessage =
                          response.response.data.Message;
                      }
                    }
                  }
                })
                .catch((error) => {
                  if (process.env.NODE_ENV == 'development') {
                    console.log(
                      "Error Message in catch of response updateUser",
                      error.data.Message
                    );
                  }
                });
            } catch (error) {
              if (process.env.NODE_ENV == 'development') {
                console.log("Error Message is", error.message);
              }
            }
          }
        } else {
          this.pushToSubscriptionPage();
        }
      }
    },
    async pushToSubscriptionPage() {
      this.$router.push({
        name: "subscription",
        params: { productcode: this.ProductCode },
        query: { offerCode: this.$route.query.offerCode }
      });
    },
    populateUpdateCustomerCommand(Email) {
      const updateCommand = {
        customerId: this.CustomerID,
        firstName: this.firstname,
        lastName: this.lastname,
        email: this.email,
        uimId: this.userID,
        metadata: {
          mstar_fname: this.firstname,
          mstar_lname: this.lastname,
          mstar_product: "newsletters",
        },
      };
      if (this.email !== Email) {
        updateCommand.password = this.password;
      }
      return updateCommand;
    },
    async onlyForInvestorUser(userID){
      const searchCustomerApiUri = process.env.VUE_APP_BackEndAPI + process.env.VUE_APP_SearchCustomer;
      const urlInvestor = searchCustomerApiUri.replace("{product}", "investor").replace("{uimId}", userID);
      const result = await fetchData(urlInvestor);

      if (result.status === 200 && Array.isArray(result.data) && result.data.length > 0) {
        this.isInvestorUser = true;
        this.$session.set("IsInvestorUser", true);
      }
    },  
  },
  async mounted() {
    this.getSelectedProduct(this.$route.params.productcode);
    this.LoggedIn = this.$auth0.isAuthenticated && this.$session.exists();
    this.$store.state.user.LoggedIn = this.LoggedIn;
    window.scrollTo(0, 0);
    this.$store.state.layout.isHeaderRequired = false;
    this.$store.state.layout.isFooterRequired = false;
    this.ProductCode = this.$route.params.productcode;
    if (this.$store.state.user.LoggedIn) {
      this.IsUserInStripe = this.$session.get("IsUserInStripe");
      this.existingUser = true;
      (this.disabled = true),
        (this.productDataRoles =
          this.$auth0.user["https://morningstar.com/uim_roles"]);
      if (this.productDataRoles != undefined && this.productDataRoles != null) {
        this.MEIAccess = this.productDataRoles.includes("EI_MEMBER");
        this.MDIAccess = this.productDataRoles.includes("DI_MEMBER");
        this.MSIAccess = this.productDataRoles.includes("SI_MEMBER");
        this.MFIAccess = this.productDataRoles.includes("FI_MEMBER");
      }
      this.userEmail = this.$auth0.user["email"];
      this.userID = this.$auth0.user["https://morningstar.com/mstar_id"];
      if (this.ProductCode == "MSI" && this.MSIAccess) {
        this.$router.push({
          name: "subscriptionexists",
          params: { productcode: this.ProductCode },
          query: { offerCode: this.$route.query.offerCode }
        });
      } else if (this.ProductCode == "MDI" && this.MDIAccess) {
        this.$router.push({
          name: "subscriptionexists",
          params: { productcode: this.ProductCode },
          query: { offerCode: this.$route.query.offerCode }
        });
      } else if (this.ProductCode == "MFI" && this.MFIAccess) {
        this.$router.push({
          name: "subscriptionexists",
          params: { productcode: this.ProductCode },
          query: { offerCode: this.$route.query.offerCode }
        });
      } else if (this.ProductCode == "MEI" && this.MEIAccess) {
        this.$router.push({
          name: "subscriptionexists",
          params: { productcode: this.ProductCode },
          query: { offerCode: this.$route.query.offerCode }
        });
      }      
    } 
    await this.populateForm();
    this.isLoading = false;
  },
  
  watch: {
    firstname(newVal, oldVal) {
      if (this.password != null && this.password != " ") {
        this.passwordError = false;
        this.validatePasswordError = false;
      }
      if (newVal != oldVal) {
        if (newVal == "" || newVal.length > 60 || newVal == null) {
          this.firstNameError = true;
        } else {
          this.firstNameError = false;
        }
      }
    },
    lastname(newVal, oldVal) {
      if (newVal != oldVal) {
        if (newVal == "" || newVal.length > 60 || newVal == null) {
          this.lastNameError = true;
        } else {
          this.lastNameError = false;
        }
      }
    },
    email(newVal, oldVal) {
      if (newVal != oldVal) {
        if (newVal == "" || newVal == null) {
          this.emailError = true;
          this.emailErrorMessage = "Email is required";
        } else if (
          newVal.length > 60 ||
          !/^[^@]+@\w+(\.\w+)+\w$/.test(newVal)
        ) {
          this.emailError = true;
          this.emailErrorMessage = "Email is not in correct format";
        } else {
          this.emailError = false;
        }
      }
    },
    password(newVal, oldVal) {
      this.verifypasswordError = false;
      if (newVal != oldVal) {
        if (newVal == null || newVal == "") {
          this.passwordError = true;
          this.validatePasswordError = false;
          this.passwordErrorMessage = "Password is required";
        } else if (!this.validatePassword(newVal)) {
          this.passwordError = false;
          this.validatePasswordError = true;
        } else {
          this.validatePasswordError = false;
        }
      }
    },
    verifypassword(newVal, oldVal) {
      if (newVal != oldVal) {
        if (newVal == null || newVal == "" || this.password != newVal) {
          this.verifypasswordError = true;
        } else {
          this.verifypasswordError = false;
        }
      }
    },
    agreementChecked(newVal, oldVal) {
      if (newVal != oldVal) {
        if (!newVal) {
          this.agreetermError = true;
        } else {
          this.agreetermError = false;
        }
      }
    },
  },
};
</script>
<style scoped>
.required {
  color: #ca0000;
  text-decoration: none;
}

.createaccount-main {
  height: 100%;
  min-height: 100vh;
  background: #f2f2f2;
}

.createaccount-register-page-bottom {
  background: linear-gradient(
    360deg,
    #ffffff 12.47%,
    rgba(255, 255, 255, 0) 112.75%
  );
  opacity: 1;
  z-index: 2;
  transform: rotate(180deg);
  height: 50px;
}

.createaccount-loader {
  position: absolute;
  width: "276px";
  height: "69px";
  left: 38.92%;
  right: 37.92%;
}

.createaccount-signSection {
  margin-top: 5% !important;
}

.createaccount-print-registration-section {
  margin-top: 5% !important;
}

.agreetermError {
  margin-top: 5% !important;
}

.createaccount-product-logo {
  margin-left: 4%;
  margin-top: 1%;
}

.createaccount-registration-main {
  max-width: 1280px;
  padding: auto;
  margin: auto;
}

.createaccount-registration-form {
  max-width: 480px;
  margin: 0 auto;
  background: #fff;
  height: 100%;
  min-height: 100vh;
}

.createaccount-registration-form-content-container {
  width: 380px;
  margin: 0 auto;
}

.createaccount-registration-page-title {
  margin-top: 0px;
  margin-bottom: 0px;
  height: 44px;
  padding-top: 57px;
  font-weight: 250;
  font-size: 28px;
  line-height: 34px;
}

.createaccount-registration-top-part-text {
  width: 100%;
  height: 177px;
  font-weight: 300;
  font-size: 16px;
  line-height: 21px;
  color: #000000;
}

.createaccount-registration-form-text-first {
  margin-top: 20%;
}

.createaccount-registration-hyperlink-test {
  cursor: pointer;
  text-decoration: none;
  border-bottom: 1px solid #000000;
  color: #000000;
}

.createaccount-registration-form-fields {
  width: 100%;
  margin-top: 5% !important;
  font-size: 16px;
  line-height: 21px;
  color: #1e1e1e;
}

.createaccount-registration-form-fields-after-login {
  width: 100%;
  margin-top: -10% !important;
  font-size: 16px;
  line-height: 21px;
  color: #1e1e1e;
}

.createaccount-registration-password-div {
  width: 100%;
  height: 88px;
  margin-left: 0px;
}

.registration-password-text {
  font-weight: 400;
  width: 100%;
  font-size: 14px;
  line-height: 18px;
  color: #000000;
  margin-top: 10px;
  margin-bottom: 0px;
}

.registration-password-text-error {
  width: 100%;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #000000;
  margin-top: 10px;
  margin-bottom: 0px;
  color: red;
}

.createaccount-registration-border {
  margin-top: 31px;
  width: 100%;
  border-bottom: 1px solid #cccccc;
}

.createaccount-registration-checkboxes {
  margin-top: 10px;
  width: 100%;
}

.createaccount-registration-button-container {
  height: 48px;
  margin-top: 40px;
  width: 100%;
}

.createaccount-registration-link {
  border-bottom: 1px solid #000000;
  color: #000000;
}

.createaccount-registration-terms-link {
  color: #1e1e1e;
}

.createaccount-registration-link {
  border: none;
  cursor: default;
  font-weight: 300;
  font-size: 20px;
  line-height: 23px;
  color: #ffffff;
  text-decoration: none;
}

.continue-button {
  margin-left: auto;
  order: 2;
}

.FormsError {
  font-size: 16px;
  font-weight: 300;
  line-height: 21px;
  color: #ca0000;
  display: block;
  margin-top: 2%;
}

@media only screen and (max-width: 480px) {
  .createaccount-registration-form-content-container {
    width: 90%;
    margin: 0 auto;
  }
  .createaccount-registration-page-title {
    padding-top: 28px;
    font-weight: 250;
    font-size: 28px;
    line-height: 34px;
  }
  .createaccount-registration-form-text-first {
    margin-top: 10%;
  }
}
</style>
<style>
.createaccount-registration-form-fields
  .mds-label.mds-label--large
  .mds-label__text {
  font-size: 16px !important;
  font-weight: 300 !important;
  line-height: 21px !important;
  font-weight: 700 !important;
  margin-bottom: 2% !important;
}

.createaccount-registration-form-fields-after-login
  .mds-label.mds-label--large
  .mds-label__text {
  font-size: 16px !important;
  font-weight: 300 !important;
  line-height: 21px !important;
  font-weight: 700 !important;
  margin-bottom: 2% !important;
}
.createaccount-registration-checkboxes .mds-checkbox__visible-wrap {
  box-shadow: none !important;
}
</style>